import React, { memo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, Database, Globe, Lock, Clock, Circle, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const capabilityIcons = {
  'Net.HTTP': Globe,
  'Storage.Local': Database,
  'Storage.KV': Database,
  'Storage.DO': Database,
  'Storage.Supabase': Database,
  'Auth.OAuth.Google': Lock,
  'Crypto.VerifyJWT': Lock,
  'Clock.Now': Clock,
  'Queue.Offline': Database,
  'Log.Trace': Zap,
  'Compile.Codegen': Zap,
};

const capabilityColors = {
  'Net.HTTP': 'text-blue-400 bg-blue-500/20',
  'Storage.Local': 'text-emerald-400 bg-emerald-500/20',
  'Storage.KV': 'text-emerald-400 bg-emerald-500/20',
  'Storage.DO': 'text-emerald-400 bg-emerald-500/20',
  'Storage.Supabase': 'text-emerald-400 bg-emerald-500/20',
  'Auth.OAuth.Google': 'text-yellow-400 bg-yellow-500/20',
  'Crypto.VerifyJWT': 'text-yellow-400 bg-yellow-500/20',
  'Clock.Now': 'text-cyan-400 bg-cyan-500/20',
  'Queue.Offline': 'text-orange-400 bg-orange-500/20',
  'Log.Trace': 'text-purple-400 bg-purple-500/20',
  'Compile.Codegen': 'text-pink-400 bg-pink-500/20',
};

/**
 * Mini child state component for compound state visualization
 */
const ChildStateBox = ({ name, isInitial, isActive }) => (
  <div className={cn(
    "px-2 py-1 rounded text-[10px] font-mono border transition-all",
    isActive 
      ? "bg-green-500/20 text-green-300 border-green-500/50 shadow-sm shadow-green-500/20" 
      : isInitial
        ? "bg-green-500/10 text-green-400/80 border-green-500/30"
        : "bg-slate-800/50 text-gray-400 border-white/10"
  )}>
    <div className="flex items-center space-x-1">
      {isInitial && <Circle className="w-2 h-2 fill-green-400 text-green-400" />}
      {isActive && <Circle className="w-2 h-2 fill-green-300 text-green-300 animate-pulse" />}
      <span>{name}</span>
    </div>
  </div>
);

const StateNode = memo(({ data, isConnectable }) => {
  const { 
    label, 
    isInitial, 
    isFinal,
    isCompound,
    childStates = [],
    internalInitial = '',
    capabilities = [], 
    entryEffects = [], 
    exitEffects = [],
    isLiveActive = false,
    activeChildState = null // For nested state tracking
  } = data;

  const [isExpanded, setIsExpanded] = useState(true);
  
  const hasEffects = entryEffects.length > 0 || exitEffects.length > 0;
  const uniqueCapabilities = [...new Set(capabilities)];

  // Regular atomic state
  if (!isCompound) {
    return (
      <div
        data-testid={`state-node-${label}`}
        data-state-kind={isFinal ? 'final' : 'atomic'}
        data-is-final={isFinal ? 'true' : 'false'}
        className={cn(
          "machine-node-custom transition-all duration-300 relative overflow-visible",
          isFinal
            ? "border-red-500/70 bg-gradient-to-b from-red-500/10 via-slate-900 to-slate-950 shadow-[0_10px_24px_rgba(127,29,29,0.28)]"
            : "border-indigo-500/30",
          isLiveActive && "ring-2 ring-green-400 ring-offset-2 ring-offset-slate-900 shadow-lg shadow-green-500/20"
        )}
      >
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="w-2 h-2 !bg-blue-400"
        />
        {isFinal && (
          <div
            data-testid={`state-badge-final-${label}`}
            className="absolute -top-2 left-2 z-10 rounded-full border border-red-400/60 bg-slate-950 px-2 py-0.5 text-[8px] font-semibold tracking-[0.22em] text-red-300 shadow-lg shadow-red-950/40"
          >
            FINAL
          </div>
        )}

        <div className="px-3 py-2 text-center min-w-[140px]">
          {/* Live Active Indicator */}
          {isLiveActive && (
            <div className="absolute -top-1 -right-1 flex items-center justify-center">
              <Circle className="w-3 h-3 text-green-400 fill-green-400 animate-pulse" />
            </div>
          )}
          
          {/* State name */}
          <div className={cn(
            "font-semibold text-sm mb-1",
            isLiveActive && "text-green-400"
          )}>
            {label}
          </div>
          
          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-1 mb-1">
            {isInitial && (
              <div className="text-[9px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded border border-green-500/30">
                INITIAL
              </div>
            )}
            {isFinal && (
              <div className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded border border-red-500/30">
                FINAL
              </div>
            )}
            {isLiveActive && (
              <div className="text-[9px] px-1.5 py-0.5 bg-green-500/30 text-green-300 rounded border border-green-400/50 font-medium">
                ACTIVE
              </div>
            )}
          </div>

          {/* Capability icons */}
          {uniqueCapabilities.length > 0 && (
            <div className="flex items-center justify-center space-x-1 mt-1.5 border-t border-white/10 pt-1.5">
              {uniqueCapabilities.slice(0, 4).map((capName, idx) => {
                const Icon = capabilityIcons[capName] || Zap;
                const colorClass = capabilityColors[capName] || 'text-purple-400 bg-purple-500/20';
                return (
                  <div
                    key={idx}
                    className={cn("w-4 h-4 rounded flex items-center justify-center", colorClass)}
                    title={capName}
                  >
                    <Icon className="w-2.5 h-2.5" />
                  </div>
                );
              })}
              {uniqueCapabilities.length > 4 && (
                <div className="text-[9px] text-gray-500" title={`+${uniqueCapabilities.length - 4} more`}>
                  +{uniqueCapabilities.length - 4}
                </div>
              )}
            </div>
          )}

          {/* Effect indicators */}
          {hasEffects && (
            <div className="flex items-center justify-center space-x-1 mt-1 text-[9px]">
              {entryEffects.length > 0 && (
                <div className="px-1 py-0.5 bg-green-500/10 text-green-400 rounded" title={`${entryEffects.length} entry effect(s)`}>
                  →{entryEffects.length}
                </div>
              )}
              {exitEffects.length > 0 && (
                <div className="px-1 py-0.5 bg-red-500/10 text-red-400 rounded" title={`${exitEffects.length} exit effect(s)`}>
                  ←{exitEffects.length}
                </div>
              )}
            </div>
          )}
        </div>

        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          className="w-2 h-2 !bg-blue-400"
        />
      </div>
    );
  }

  // Compound state with nested visualization
  return (
    <div
      data-testid={`state-node-${label}`}
      data-state-kind="compound"
      data-is-compound="true"
      className={cn(
        "transition-all duration-300 rounded-lg relative overflow-visible",
        "bg-gradient-to-b from-yellow-500/10 via-slate-900 to-slate-950",
        "border-2 border-yellow-400/60 shadow-[0_10px_24px_rgba(113,63,18,0.28)]",
        isLiveActive && "ring-2 ring-green-400 ring-offset-2 ring-offset-slate-900 shadow-lg shadow-green-500/20"
      )}
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-2 h-2 !bg-yellow-400"
      />
      {isCompound && (
        <div
          data-testid={`state-badge-compound-${label}`}
          className="absolute -top-2 left-2 z-10 rounded-full border border-yellow-300/70 bg-slate-950 px-2 py-0.5 text-[8px] font-semibold tracking-[0.22em] text-yellow-200 shadow-lg shadow-amber-950/40"
        >
          COMPOUND
        </div>
      )}
      
      {/* Compound State Header */}
      <div 
        className="px-3 py-2 border-b border-yellow-500/30 cursor-pointer hover:bg-yellow-500/10"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between min-w-[180px]">
          <div className="flex items-center space-x-2">
            <Layers className="w-4 h-4 text-yellow-400" />
            <span className={cn(
              "font-semibold text-sm",
              isLiveActive ? "text-green-400" : "text-yellow-300"
            )}>
              {label}
            </span>
          </div>
          <div className="flex items-center space-x-1">
            {isLiveActive && (
              <Circle className="w-3 h-3 text-green-400 fill-green-400 animate-pulse" />
            )}
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-yellow-400/70" />
            ) : (
              <ChevronRight className="w-4 h-4 text-yellow-400/70" />
            )}
          </div>
        </div>
        
        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <div className="text-[9px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded border border-yellow-500/30">
            COMPOUND
          </div>
          {isInitial && (
            <div className="text-[9px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded border border-green-500/30">
              INITIAL
            </div>
          )}
          {isFinal && (
            <div className="text-[9px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded border border-red-500/30">
              FINAL
            </div>
          )}
          {isLiveActive && (
            <div className="text-[9px] px-1.5 py-0.5 bg-green-500/30 text-green-300 rounded border border-green-400/50">
              ACTIVE
            </div>
          )}
          <div className="text-[9px] px-1.5 py-0.5 bg-slate-700/50 text-gray-400 rounded">
            {childStates.length} substates
          </div>
        </div>
      </div>

      {/* Nested States Container (Expanded View) */}
      {isExpanded && childStates.length > 0 && (
        <div className="p-2 space-y-1.5 bg-slate-900/30">
          {/* Internal state diagram */}
          <div className="flex flex-wrap gap-1.5 justify-center">
            {childStates.map((childName) => (
              <ChildStateBox 
                key={childName}
                name={childName}
                isInitial={childName === internalInitial}
                isActive={childName === activeChildState}
              />
            ))}
          </div>
          
          {/* Internal initial indicator */}
          {internalInitial && (
            <div className="text-[9px] text-center text-yellow-400/60 pt-1 border-t border-yellow-500/20">
              <span className="text-gray-500">internal initial →</span> {internalInitial}
            </div>
          )}
        </div>
      )}

      {/* Collapsed preview */}
      {!isExpanded && childStates.length > 0 && (
        <div className="px-3 py-1.5 text-[9px] text-yellow-400/70">
          {childStates.slice(0, 3).join(' • ')}
          {childStates.length > 3 && ` +${childStates.length - 3}`}
        </div>
      )}

      {/* Capability icons */}
      {uniqueCapabilities.length > 0 && (
        <div className="flex items-center justify-center space-x-1 px-3 py-1.5 border-t border-yellow-500/20">
          {uniqueCapabilities.slice(0, 4).map((capName, idx) => {
            const Icon = capabilityIcons[capName] || Zap;
            const colorClass = capabilityColors[capName] || 'text-purple-400 bg-purple-500/20';
            return (
              <div
                key={idx}
                className={cn("w-4 h-4 rounded flex items-center justify-center", colorClass)}
                title={capName}
              >
                <Icon className="w-2.5 h-2.5" />
              </div>
            );
          })}
        </div>
      )}

      {/* Effect indicators */}
      {hasEffects && (
        <div className="flex items-center justify-center space-x-1 px-3 py-1 text-[9px] border-t border-yellow-500/20">
          {entryEffects.length > 0 && (
            <div className="px-1 py-0.5 bg-green-500/10 text-green-400 rounded">
              →{entryEffects.length}
            </div>
          )}
          {exitEffects.length > 0 && (
            <div className="px-1 py-0.5 bg-red-500/10 text-red-400 rounded">
              ←{exitEffects.length}
            </div>
          )}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="w-2 h-2 !bg-yellow-400"
      />
    </div>
  );
});

StateNode.displayName = 'StateNode';

export default StateNode;
